import { ListedInstrument, OrderExchangeData } from '@eih-netbroker/common';
import { useCallback } from 'react';
import { TextHeaderDialog, TextHeaderDialogProps } from '../../../../shared/components/dialog-presets/TextHeaderDialog';
import { useCreateNormalOrder, ValidatedOrder } from '../../services/order/useNormalOrderView';
import { CapturedExchangeData, OrderFlowStep, OrderFormSourceComponent } from '../../types/orderTypes';
import { OrderFormData } from '../order/OrderForm';
import { useOrderState } from '../OrderStateContext';
import { OrderModalBottomNavigationButtons } from './order-footer/OrderModalBottomNavigationButtons';
import { PriceValidationState } from './order-form/OrderForm2';
import { OrderTwoFactor } from './order-two-factor/OrderTwoFactor';
import classes from './OrderModal2.module.scss';
import { OrderModalContent } from './OrderModalContent';

export interface OrderModal2Props {
  // if true => resets the widget/modal order form (order went through for example)
  onCancel: (shouldResetFormToDefaults: boolean) => void;
  onBack: (shouldResetFormToDefaults: boolean) => void;
  onClose: (shouldResetFormToDefaults: boolean) => void;

  on2FARetry: () => Promise<void>;
  orderId?: string;
  isOrderFormInvalid: boolean;
  exchangeData?: OrderExchangeData;
  capturedExchangeData?: CapturedExchangeData;
  sourceComponent?: OrderFormSourceComponent;
  priceValidationState?: PriceValidationState;
  stopActivationPriceValidationState?: PriceValidationState;

  // State & callbacks from useOrderModalNew (owned by parent)
  orderState: OrderFormData;
  updateOrderState: (order: OrderFormData) => void;
  changeInstrument: (instrument: ListedInstrument | null) => void;
  onStopActivationPriceRevalidate: (order: OrderFormData) => Promise<void>;
  initialOrder: ValidatedOrder | null;
  modalOptions: Pick<TextHeaderDialogProps, 'headerText' | 'withHeaderDivider' | 'withFooterDivider'>;
  onConfirmHandle: (exchangeData?: OrderExchangeData) => Promise<void>;
  onContinue: (exchangeData?: OrderExchangeData) => Promise<void>;
}

export const OrderModal2 = ({
  onCancel,
  orderId,
  sourceComponent = OrderFormSourceComponent.Modal,
  exchangeData,
  capturedExchangeData,
  isOrderFormInvalid,
  onBack,
  onClose,
  on2FARetry,
  priceValidationState,
  stopActivationPriceValidationState,
  orderState,
  updateOrderState,
  changeInstrument,
  onStopActivationPriceRevalidate,
  initialOrder,
  modalOptions,
  onConfirmHandle,
  onContinue,
}: OrderModal2Props) => {
  const { isLoading: globalOrderRequestLoading } = useCreateNormalOrder();
  const { orderFlowStep } = useOrderState();
  const handleConfirm = useCallback(() => onConfirmHandle(exchangeData), [onConfirmHandle, exchangeData]);
  const handleContinue = useCallback(() => onContinue(exchangeData), [onContinue, exchangeData]);
  const handleClose = useCallback(() => onCancel(false), [onCancel]);

  if (orderFlowStep === OrderFlowStep.TwoFactor) {
    return (
      <OrderTwoFactor
        capturedExchangeData={capturedExchangeData}
        exchangeData={exchangeData}
        onBack={onBack}
        onContinue={onContinue}
        onClose={onClose}
        on2FARetry={on2FARetry}
      />
    );
  }

  return (
    <TextHeaderDialog
      headerText={modalOptions.headerText}
      withHeaderDivider={modalOptions.withHeaderDivider}
      withFooterDivider={modalOptions.withFooterDivider}
      closeButtonAction={handleClose}
      closeButtonDisabled={globalOrderRequestLoading}
      withCloseButton
      content={
        <div className={classes.orderContent}>
          <OrderModalContent
            orderState={orderState}
            updateOrderState={updateOrderState}
            changeInstrument={changeInstrument}
            onStopActivationPriceRevalidate={onStopActivationPriceRevalidate}
            priceValidationState={priceValidationState}
            stopActivationPriceValidationState={stopActivationPriceValidationState}
            orderId={orderId}
            orderRequestLoading={globalOrderRequestLoading}
            initialOrder={initialOrder}
            sourceComponent={sourceComponent}
            exchangeData={exchangeData}
            capturedExchangeData={capturedExchangeData}
            onBack={onBack}
          />
        </div>
      }
      footer={
        <OrderModalBottomNavigationButtons
          orderRequestLoading={globalOrderRequestLoading}
          onCancel={onCancel}
          isOrderFormInvalid={isOrderFormInvalid}
          orderState={orderState}
          onConfirmHandle={handleConfirm}
          onContinue={handleContinue}
          onBack={onBack}
          sourceComponent={sourceComponent}
        />
      }
    />
  );
};
